/* Accounts, and minting a workshop's worth of them.
   OWNED BY ITEM 6 OF docs/plan/00-prestavba-2026-08.md.

   The generator sits behind a sign-in because the portal is public and tokens
   cost money. A room, however, has no accounts — so they are made here, in one
   press, and the passwords are shown once. */

import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type Minted, type UserRow } from "../../lib/api";
import { useI18n } from "../../lib/i18n";

export function Accounts() {
  const { ui } = useI18n();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [minted, setMinted] = useState<Minted[] | null>(null);
  const [count, setCount] = useState(25);
  const [prefix, setPrefix] = useState("ucitel");
  const [trusted, setTrusted] = useState("");
  const [msg, setMsg] = useState<{ text: string; bad?: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { void api<UserRow[]>("/admin/users").then(setUsers).catch(() => {}); }, []);
  useEffect(load, [load]);

  async function mint() {
    setBusy(true);
    setMsg(null);
    try {
      setMinted(await api<Minted[]>("/admin/users/workshop", {
        method: "POST",
        body: JSON.stringify({ count, prefix }),
      }));
      load();
    } catch (err) {
      setMsg({ text: err instanceof ApiError ? err.message : ui("accounts.failed"), bad: true });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(ui("accounts.confirmRemove").replace("{prefix}", prefix))) return;
    setBusy(true);
    try {
      const { removed } = await api<{ removed: number }>(`/admin/users/workshop/${prefix}`,
                                                        { method: "DELETE" });
      setMinted(null);
      setMsg({ text: ui("accounts.removed").replace("{n}", String(removed)) });
      load();
    } catch (err) {
      setMsg({ text: err instanceof ApiError ? err.message : ui("accounts.failed"), bad: true });
    } finally {
      setBusy(false);
    }
  }

  /* One trusted account, minted the same way and shown the same once: what is
     stored is an Argon2 hash either way. */
  async function mintPower() {
    setBusy(true);
    setMsg(null);
    try {
      setMinted([await api<Minted>("/admin/users/power", {
        method: "POST",
        body: JSON.stringify({ username: trusted }),
      })]);
      setTrusted("");
      load();
    } catch (err) {
      setMsg({ text: err instanceof ApiError ? err.message : ui("accounts.failed"), bad: true });
    } finally {
      setBusy(false);
    }
  }

  async function removeUser(name: string) {
    if (!window.confirm(ui("accounts.confirmRemoveUser").replace("{name}", name))) return;
    try {
      await api<void>(`/admin/users/${name}`, { method: "DELETE" });
      load();
    } catch (err) {
      setMsg({ text: err instanceof ApiError ? err.message : ui("accounts.failed"), bad: true });
    }
  }

  const list = minted ? minted.map((m) => `${m.username}\t${m.password}`).join("\n") : "";

  return (
    <section className="adm-panel">
      <p className="adm-sub">{ui("accounts.lead")}</p>

      <div className="adm-mint">
        <label className="adm-field narrow">
          {ui("accounts.count")}
          <input type="number" min={1} max={200} value={count}
                 onChange={(e) => setCount(Number(e.target.value))} />
        </label>
        <label className="adm-field narrow">
          {ui("accounts.prefix")}
          <input value={prefix} onChange={(e) => setPrefix(e.target.value)}
                 pattern="[a-z][a-z0-9-]*" />
        </label>
        <button type="button" disabled={busy} onClick={() => void mint()}>
          {ui("accounts.mint")}
        </button>
        <button type="button" className="danger" disabled={busy} onClick={() => void remove()}>
          {ui("accounts.remove")}
        </button>
      </div>

      <h3 className="adm-h">{ui("accounts.power")}</h3>
      <p className="adm-sub">{ui("accounts.powerLead")}</p>
      <div className="adm-mint">
        <label className="adm-field">
          {ui("accounts.powerName")}
          <input value={trusted} spellCheck={false} pattern="[a-z][a-z0-9-]*"
                 onChange={(e) => setTrusted(e.target.value.trim())} />
        </label>
        <button type="button" disabled={busy || trusted.length < 3}
                onClick={() => void mintPower()}>
          {ui("accounts.powerMake")}
        </button>
      </div>

      {minted ? (
        <div className="adm-minted">
          {/* Said before the list, not after it: what is stored is an Argon2
              hash, so "show them again" has no answer and the moment to learn
              that is now. */}
          <p className="adm-warn">{ui("accounts.onceOnly")}</p>
          <textarea readOnly rows={Math.min(12, minted.length + 1)} value={list} />
          <button type="button" onClick={() => void navigator.clipboard.writeText(list)}>
            {ui("accounts.copy")}
          </button>
        </div>
      ) : null}

      {msg ? <p className={"adm-msg" + (msg.bad ? " bad" : "")}>{msg.text}</p> : null}

      <h3 className="adm-h">{ui("accounts.existing")}</h3>
      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th>{ui("accounts.name")}</th><th>{ui("accounts.role")}</th>
              <th>{ui("accounts.lastLogin")}</th><th>{ui("accounts.runsToday")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={u.disabled ? "off" : ""}>
                <td>{u.username}</td>
                <td>{ui(`role.${u.role}`)}</td>
                {/* The offset is noise on a screen where every row is the same one. */}
                <td>{u.last_login_at ? u.last_login_at.replace("T", " ").slice(0, 16) : "—"}</td>
                <td>{u.runs_today || "—"}</td>
                <td>
                  {/* An administrator is removed from the command line, never
                      from a page somebody could be locked out of. */}
                  {u.role === "admin" ? null : (
                    <button type="button" className="danger"
                            onClick={() => void removeUser(u.username)}>
                      {ui("accounts.removeUser")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
